import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Star } from "lucide-react";
import ReviewModal from "@/components/ReviewModal";

const STATUS = {
  pending: { label: "Bekliyor", color: "bg-amber-500" },
  accepted: { label: "Kabul Edildi", color: "bg-[hsl(var(--success))]" },
  driver_arriving: { label: "Kurtarıcı Yolda", color: "bg-primary text-[hsl(var(--brand-black))]" },
  arrived: { label: "Kurtarıcı Geldi", color: "bg-primary text-[hsl(var(--brand-black))]" },
  completed: { label: "Tamamlandı", color: "bg-slate-700" },
  rejected: { label: "Reddedildi", color: "bg-destructive" },
  cancelled: { label: "İptal Edildi", color: "bg-slate-500" },
};

export default function HelpHistoryPage() {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [reviewTarget, setReviewTarget] = useState(null);

  const load = async () => {
    const { data } = await api.get("/help-requests/history");
    // Enrich with business names for display via a single batch call: reuse business slug/name embedded when we can; else look up
    const bizIds = [...new Set(data.map((d) => d.business_id))];
    const bizMap = {};
    await Promise.all(bizIds.map((id) => api.get(`/businesses/${id}`).then((r) => { bizMap[id] = r.data; }).catch(() => {})));
    setItems(data.map((d) => ({ ...d, business: bizMap[d.business_id] })));
  };

  useEffect(() => {
    if (loading) return;
    if (!user) { nav("/giris"); return; }
    if (user.role !== "user") { nav("/"); return; }
    load();
  }, [user, loading, nav]);

  return (
    <div className="mx-auto max-w-3xl px-4 py-6">
      <h1 className="text-2xl font-bold tracking-tight mb-4">Yardım Taleplerim</h1>
      <div className="grid gap-2">
        {items.length === 0 && <p className="text-muted-foreground text-sm">Henüz yardım talebi oluşturmadınız.</p>}
        {items.map((r) => {
          const s = STATUS[r.status] || { label: r.status, color: "bg-slate-500" };
          return (
            <div key={r.id} className="rounded-2xl border p-4" data-testid={`hist-${r.id}`}>
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="min-w-0">
                  <div className="font-semibold">{r.business?.name || "—"}</div>
                  <div className="text-xs text-muted-foreground">{new Date(r.created_at).toLocaleString("tr-TR")}</div>
                  <div className="text-sm mt-1">{r.request_type} · {r.customer_vehicle_type}</div>
                  {r.agreed_price != null && (
                    <div className="text-sm text-primary font-semibold mt-1" data-testid={`hist-price-${r.id}`}>
                      Anlaşılan Fiyat: {Number(r.agreed_price).toLocaleString("tr-TR")} ₺
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <Badge className={`${s.color} text-white`}>{s.label}</Badge>
                  {r.status === "completed" && (
                    r.review_id ? (
                      <span className="inline-flex items-center gap-1 text-primary text-sm" data-testid={`hist-reviewed-${r.id}`}>
                        <Star className="w-4 h-4 fill-current" /> Değerlendirildi
                      </span>
                    ) : (
                      <Button size="sm" onClick={() => setReviewTarget(r)} className="rounded-full bg-primary text-[hsl(var(--brand-black))] hover:bg-primary/90 font-bold" data-testid={`hist-review-btn-${r.id}`}>
                        Değerlendir
                      </Button>
                    )
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <ReviewModal
        open={!!reviewTarget}
        onOpenChange={(v) => !v && setReviewTarget(null)}
        request={reviewTarget}
        onDone={() => { setReviewTarget(null); load(); }}
      />
    </div>
  );
}
